// W2Cook Billing Dashboard — Shared UI primitives

import { useState, CSSProperties } from "react";
import { tokens as T } from "../theme/tokens";

// ── Pill badge ────────────────────────────────────────────────────────────────
export function Pill({ label, color }: { label: string; color: string }) {
  return (
    <span style={{
      fontSize: 11, padding: "2px 8px", borderRadius: 4,
      background: `${color}18`, color, border: `1px solid ${color}30`,
      whiteSpace: "nowrap",
    }}>{label}</span>
  );
}

// ── Button ────────────────────────────────────────────────────────────────────
type BtnVariant = "ghost" | "primary" | "danger" | "subtle";
interface BtnProps {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: BtnVariant;
  style?: CSSProperties;
  disabled?: boolean;
}

const VARIANT_STYLES: Record<BtnVariant, CSSProperties> = {
  ghost:   { background: "none",       border: `1px solid ${T.border}`,      color: T.textMuted },
  primary: { background: T.ember,      border: "none",                        color: "#fff", fontWeight: 600 },
  danger:  { background: "none",       border: `1px solid ${T.ember}40`,      color: T.ember },
  subtle:  { background: T.surfaceAlt, border: `1px solid ${T.border}`,       color: T.text },
};

export function Btn({ children, onClick, variant = "ghost", style: s, disabled }: BtnProps) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        cursor: disabled ? "not-allowed" : "pointer",
        borderRadius: 8, fontSize: 13, padding: "8px 14px",
        transition: "all 0.15s", fontFamily: "inherit", opacity: disabled ? 0.5 : 1,
        ...VARIANT_STYLES[variant], ...s,
      }}
    >{children}</button>
  );
}

// ── Text field ────────────────────────────────────────────────────────────────
interface FieldProps {
  label: string;
  value: string | number;
  onChange: (v: string) => void;
  placeholder?: string;
  secret?: boolean;
  rows?: number;
  type?: string;
}

export function Field({ label, value, onChange, placeholder = "", secret = false, rows, type = "text" }: FieldProps) {
  const [show, setShow] = useState(false);
  const inputStyle: CSSProperties = {
    width: "100%", boxSizing: "border-box",
    background: T.bg, border: `1px solid ${T.border}`,
    borderRadius: 8, color: T.text,
    padding: secret ? "9px 36px 9px 12px" : "9px 12px",
    fontSize: 13, outline: "none", fontFamily: "inherit",
  };
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, color: T.textMuted, letterSpacing: "0.08em", marginBottom: 5 }}>{label}</div>
      {rows ? (
        <textarea
          value={String(value)} onChange={(e) => onChange(e.target.value)}
          rows={rows} placeholder={placeholder}
          style={{ ...inputStyle, padding: "9px 12px", resize: "vertical" }}
        />
      ) : (
        <div style={{ position: "relative" }}>
          <input
            type={secret && !show ? "password" : type}
            value={String(value)} onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder} style={inputStyle}
          />
          {secret && (
            <button
              onClick={() => setShow((p) => !p)}
              style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: T.textDim, cursor: "pointer", fontSize: 13 }}
            >{show ? "🙈" : "👁"}</button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Modal shell ───────────────────────────────────────────────────────────────
interface ModalProps {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  width?: number;
}

export function Modal({ title, onClose, children, width = 420 }: ModalProps) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.78)",
      zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div style={{
        background: T.surface, border: `1px solid ${T.border}`,
        borderRadius: 14, padding: 28, width, maxWidth: "100%",
        maxHeight: "90vh", overflowY: "auto",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 22 }}>
          <span style={{ fontSize: 17, fontWeight: 600, color: T.text }}>{title}</span>
          <button onClick={onClose} style={{ background: "none", border: "none", color: T.textMuted, fontSize: 22, cursor: "pointer", lineHeight: 1 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Section header ────────────────────────────────────────────────────────────
export function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 11, color: T.textMuted, letterSpacing: "0.08em", marginBottom: 10 }}>
      {children}
    </div>
  );
}
